import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ArrowLeft, BarChart3, Building2, CheckCircle2, ChevronDown, CreditCard, Download, LoaderCircle, Mail, RefreshCw, Search, ShieldAlert, ShieldCheck, Trash2, TrendingDown, TrendingUp, UserMinus, Users, Webhook, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";

type AdminTab = "overview" | "spenders" | "change" | "failures" | "users" | "workspaces" | "events" | "admins";

export type Period = { days?: number; from?: string; to?: string } | null;

export function periodQuery(period: Period): string {
  if (!period) return "";
  if (period.days) return `days=${period.days}`;
  const parts: string[] = [];
  if (period.from) parts.push(`from=${period.from}`);
  if (period.to) parts.push(`to=${period.to}`);
  return parts.join("&");
}

function withPeriod(path: string, period: Period, extra = ""): string {
  const query = [periodQuery(period), extra].filter(Boolean).join("&");
  return query ? `${path}?${query}` : path;
}

// Authenticated download (api() expects JSON; reports come back as files).
async function downloadReport(config: Record<string, unknown>): Promise<void> {
  const session = supabase ? (await supabase.auth.getSession()).data.session : null;
  const response = await fetch("/api/admin/reports", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { message?: string };
    throw new Error(payload.message || "The report could not be generated.");
  }
  const filename = response.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || "report";
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

type ReportGroup = "account" | "account_day" | "client" | "client_day" | "day";
type ReportOptions = { groups: Array<{ key: ReportGroup; label: string; default_columns: string[] }>; columns: Array<{ key: string; label: string; groups: ReportGroup[] }> };
type ReportPeriod = { days?: number; from?: string; to?: string };
type ReportConfig = { scope: "all" | "accounts" | "clients"; accounts: string[]; users: string[]; period: ReportPeriod; group: ReportGroup; columns: string[]; include_zero: boolean; sort: { key: string; dir: "asc" | "desc" }; format: "csv" | "json" };
type ReportPreview = { columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string | number | null>>; total: number; from: string; to: string };
const REPORT_STORAGE_KEY = "koast-admin-report-config";
const REPORT_NAMES: Record<ReportGroup, string> = { client: "Spend by client", account: "Spend by ad account", account_day: "Daily spend per ad account", client_day: "Daily spend per client", day: "Daily platform totals" };
const REPORT_PERIODS: Array<{ key: string; label: string; period: ReportPeriod }> = [
  { key: "7", label: "Last 7 days", period: { days: 7 } }, { key: "30", label: "Last 30 days", period: { days: 30 } }, { key: "90", label: "Last 90 days", period: { days: 90 } },
  { key: "180", label: "Last 180 days", period: { days: 180 } }, { key: "365", label: "Last 12 months", period: { days: 365 } },
];
const MONEY_COLUMNS = new Set(["spend", "profit", "avg_daily_spend", "daily_budget"]);
const SUMMABLE_COLUMNS = new Set(["spend", "profit", "payment_failures", "launch_failures", "client_accounts", "accounts_with_spend"]);

function defaultReportConfig(period: Period): ReportConfig {
  return { scope: "all", accounts: [], users: [], period: period?.days ? { days: period.days } : period?.from || period?.to ? { from: period.from, to: period.to } : { days: 30 }, group: "client", columns: [], include_zero: true, sort: { key: "spend", dir: "desc" }, format: "csv" };
}

function formatCell(value: string | number | null, key: string): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") return MONEY_COLUMNS.has(key) ? money(value) : value.toLocaleString();
  return String(value);
}

// Popover rendered at the document level so neither the dialog's overflow
// nor the table's scroll container can clip it. Positioned from the trigger's
// on-screen rect and flipped upward when there is no room below.
function Popover({ open, anchor, onClose, children, align = "left" }: { open: boolean; anchor: HTMLElement | null; onClose: () => void; children: ReactNode; align?: "left" | "right" }) {
  const [box, setBox] = useState<{ top?: number; bottom?: number; left?: number; right?: number } | null>(null);
  useEffect(() => {
    if (!open || !anchor) { queueMicrotask(() => setBox(null)); return; }
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const up = window.innerHeight - rect.bottom < 340 && rect.top > 340;
      setBox({
        ...(up ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
        ...(align === "right" ? { right: Math.max(8, window.innerWidth - rect.right) } : { left: Math.max(8, rect.left) }),
      });
    };
    queueMicrotask(place);
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, anchor, align]);
  if (!open || !box) return null;
  return createPortal(<>
    <button type="button" className="report-pop__backdrop" aria-label="Close" onClick={onClose} />
    <div className="report-pop" role="dialog" style={box}>{children}</div>
  </>, document.body);
}

function ReportBuilder({ period, onClose, onError }: { period: Period; onClose: () => void; onError: (message: string) => void }) {
  const [options, setOptions] = useState<ReportOptions | null>(null);
  const [accounts, setAccounts] = useState<AdminWorkspace[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [config, setConfig] = useState<ReportConfig>(() => {
    try {
      const stored = window.localStorage.getItem(REPORT_STORAGE_KEY);
      if (stored) return { ...defaultReportConfig(period), ...JSON.parse(stored) as Partial<ReportConfig>, period: defaultReportConfig(period).period };
    } catch { /* defaults */ }
    return defaultReportConfig(period);
  });
  const [preview, setPreview] = useState<ReportPreview | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [openMenu, setOpenMenu] = useState<"" | "scope" | "columns" | "more" | "custom">("");
  const [pickerQuery, setPickerQuery] = useState("");
  const [customFrom, setCustomFrom] = useState(config.period.from || "");
  const [customTo, setCustomTo] = useState(config.period.to || "");
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [scopeEl, setScopeEl] = useState<HTMLButtonElement | null>(null);
  const [periodEl, setPeriodEl] = useState<HTMLSelectElement | null>(null);
  const [columnsEl, setColumnsEl] = useState<HTMLButtonElement | null>(null);
  const [moreEl, setMoreEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<ReportOptions>("/api/admin/reports/options").then((payload) => { if (!cancelled) setOptions(payload); }).catch((caught) => onError(caught instanceof Error ? caught.message : "The report options could not be loaded."));
    api<AdminWorkspacesResponse>("/api/admin/workspaces").then((payload) => { if (!cancelled) setAccounts(payload.workspaces); }).catch(() => undefined);
    api<{ users: AdminUser[] }>("/api/admin/users").then((payload) => { if (!cancelled) setUsers(payload.users); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [onError]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") { if (openMenu) setOpenMenu(""); else onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu, onClose]);

  const applicable = useMemo(() => (options?.columns || []).filter((column) => column.groups.includes(config.group)), [options, config.group]);
  const columns = useMemo(() => {
    const chosen = config.columns.filter((key) => applicable.some((column) => column.key === key));
    return chosen.length ? chosen : (options?.groups.find((group) => group.key === config.group)?.default_columns || []);
  }, [config.columns, config.group, applicable, options]);
  const labelOf = (key: string) => applicable.find((column) => column.key === key)?.label || key;
  const requestBody = useMemo(() => ({ ...config.period, group: config.group, columns, accounts: config.scope === "accounts" ? config.accounts : [], users: config.scope === "clients" ? config.users : [], include_zero: config.include_zero, sort: config.sort }), [config, columns]);

  useEffect(() => {
    if (!options) return;
    let cancelled = false;
    setLoading(true);
    const handle = window.setTimeout(() => {
      api<ReportPreview>("/api/admin/reports", { method: "POST", body: JSON.stringify({ ...requestBody, preview: true, limit: 25 }) })
        .then((payload) => { if (!cancelled) { setPreview(payload); setPreviewError(""); } })
        .catch((caught) => { if (!cancelled) setPreviewError(caught instanceof Error ? caught.message : "The preview could not be loaded."); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [requestBody, options]);

  const update = (patch: Partial<ReportConfig>) => setConfig((current) => ({ ...current, ...patch }));
  const setColumns = (next: string[]) => update({ columns: next });
  const moveColumn = (key: string, delta: number) => { const index = columns.indexOf(key); const target = index + delta; if (index < 0 || target < 0 || target >= columns.length) return; const next = [...columns]; next.splice(index, 1); next.splice(target, 0, key); setColumns(next); };
  const toggleColumn = (key: string) => { if (columns.includes(key)) { if (columns.length > 1) setColumns(columns.filter((entry) => entry !== key)); } else setColumns([...columns, key]); };
  const sortBy = (key: string) => update({ sort: { key, dir: config.sort.key === key ? (config.sort.dir === "asc" ? "desc" : "asc") : (key === "date" || key.endsWith("_email") || key.endsWith("_name") ? "asc" : "desc") } });
  const changeGroup = (group: ReportGroup) => update({ group, columns: [], sort: { key: group.includes("day") ? "date" : "spend", dir: group.includes("day") ? "asc" : "desc" } });
  const toggleId = (list: "accounts" | "users", id: string) => setConfig((current) => ({ ...current, [list]: current[list].includes(id) ? current[list].filter((entry) => entry !== id) : [...current[list], id] }));
  const periodKey = config.period.days ? String(config.period.days) : "custom";
  const scopeLabel = config.scope === "all" ? "all ad accounts" : config.scope === "accounts" ? `${config.accounts.length || "no"} ad account${config.accounts.length === 1 ? "" : "s"}` : `${config.users.length || "no"} client${config.users.length === 1 ? "" : "s"}`;
  const download = async (format: "csv" | "json") => {
    setDownloading(format); setOpenMenu("");
    try { await downloadReport({ ...requestBody, format }); }
    catch (caught) { onError(caught instanceof Error ? caught.message : "The report could not be generated."); }
    finally { setDownloading(""); }
  };
  const copyCsv = async () => {
    setOpenMenu("");
    const full = await api<ReportPreview>("/api/admin/reports", { method: "POST", body: JSON.stringify({ ...requestBody, preview: true, limit: 50 }) }).catch(() => preview);
    if (!full) return;
    const escape = (value: string) => /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
    const text = [full.columns.map((column) => column.label).join(","), ...full.rows.map((row) => full.columns.map((column) => escape(String(row[column.key] ?? ""))).join(","))].join("\n");
    await navigator.clipboard.writeText(text).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1600); }).catch(() => undefined);
  };
  const saveDefault = () => { try { window.localStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify({ ...config, period: undefined })); setSaved(true); window.setTimeout(() => setSaved(false), 1600); } catch { /* ignore */ } };
  const totals = useMemo(() => {
    if (!preview || preview.total !== preview.rows.length) return null;
    const sums: Record<string, number> = {};
    for (const key of columns) if (SUMMABLE_COLUMNS.has(key)) sums[key] = preview.rows.reduce((sum, row) => sum + (typeof row[key] === "number" ? row[key] as number : 0), 0);
    return Object.keys(sums).length ? sums : null;
  }, [preview, columns]);
  const needle = pickerQuery.trim().toLowerCase();
  const pickerAccounts = accounts.filter((account) => !needle || account.name.toLowerCase().includes(needle) || account.owner_email.includes(needle));
  const pickerUsers = users.filter((user) => !needle || user.email.includes(needle) || user.account_names.some((name) => name.toLowerCase().includes(needle)));

  return <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
    <div className="admin-modal report" role="dialog" aria-modal="true" aria-labelledby="report-title" onClick={(event) => event.stopPropagation()}>
      <div className="report__head">
        <h3 id="report-title"><Download /> Export report</h3>
        <button className="report-close" type="button" aria-label="Close" onClick={onClose}><XCircle /></button>
      </div>

      <div className="report__sentence" aria-label="Report definition">
        <span>Show</span>
        <select className="report-select" value={config.group} aria-label="Report type" onChange={(event) => changeGroup(event.target.value as ReportGroup)}>
          {(options?.groups || []).map((group) => <option key={group.key} value={group.key}>{REPORT_NAMES[group.key]}</option>)}
        </select>
        <span>for</span>
        <span className="report-anchor">
          <button ref={setScopeEl} type="button" className={`report-select report-select--button ${config.scope !== "all" ? "is-set" : ""}`} aria-haspopup="dialog" aria-expanded={openMenu === "scope"} onClick={() => setOpenMenu(openMenu === "scope" ? "" : "scope")}>{scopeLabel} <ChevronDown /></button>
          <Popover open={openMenu === "scope"} anchor={scopeEl} onClose={() => setOpenMenu("")}>
            <div className="admin-segment report-pop__segment" role="radiogroup" aria-label="Scope">
              <button type="button" role="radio" aria-checked={config.scope === "all"} className={config.scope === "all" ? "is-active" : ""} onClick={() => update({ scope: "all" })}>All</button>
              <button type="button" role="radio" aria-checked={config.scope === "accounts"} className={config.scope === "accounts" ? "is-active" : ""} onClick={() => update({ scope: "accounts" })}>Pick ad accounts</button>
              <button type="button" role="radio" aria-checked={config.scope === "clients"} className={config.scope === "clients" ? "is-active" : ""} onClick={() => update({ scope: "clients" })}>Pick clients</button>
            </div>
            {config.scope !== "all" && <>
              <div className="report-pop__tools">
                <label className="admin-inline-search"><Search /><input autoFocus value={pickerQuery} placeholder={config.scope === "accounts" ? "Search accounts or clients" : "Search clients"} onChange={(event) => setPickerQuery(event.target.value)} /></label>
                <button className="admin-link" type="button" onClick={() => config.scope === "accounts" ? update({ accounts: [...new Set([...config.accounts, ...pickerAccounts.map((account) => account.whop_account_id)])] }) : update({ users: [...new Set([...config.users, ...pickerUsers.map((user) => user.id)])] })}>Select shown</button>
                <button className="admin-link" type="button" onClick={() => config.scope === "accounts" ? update({ accounts: [] }) : update({ users: [] })}>Clear</button>
              </div>
              <ul className="report-pop__list">
                {config.scope === "accounts" && pickerAccounts.map((account) => <li key={account.whop_account_id}><label><input type="checkbox" checked={config.accounts.includes(account.whop_account_id)} onChange={() => toggleId("accounts", account.whop_account_id)} /><span><strong>{account.name}</strong><small>{account.owner_email || "no owner email"}</small></span></label></li>)}
                {config.scope === "clients" && pickerUsers.map((user) => <li key={user.id}><label><input type="checkbox" checked={config.users.includes(user.id)} onChange={() => toggleId("users", user.id)} /><span><strong>{user.email}</strong><small>{user.account_count} ad account{user.account_count === 1 ? "" : "s"}</small></span></label></li>)}
                {((config.scope === "accounts" && pickerAccounts.length === 0) || (config.scope === "clients" && pickerUsers.length === 0)) && <li className="report-pop__empty">Nothing matches.</li>}
              </ul>
            </>}
            <div className="report-pop__foot"><button className="button button--primary button--small" type="button" onClick={() => setOpenMenu("")}>Done</button></div>
          </Popover>
        </span>
        <span>over</span>
        <span className="report-anchor">
          <select ref={setPeriodEl} className="report-select" value={periodKey} aria-label="Period" onChange={(event) => { const preset = REPORT_PERIODS.find((entry) => entry.key === event.target.value); if (preset) { update({ period: preset.period }); setOpenMenu(""); } else setOpenMenu("custom"); }}>
            {REPORT_PERIODS.map((entry) => <option key={entry.key} value={entry.key}>{entry.label.toLowerCase()}</option>)}
            <option value="custom">{periodKey === "custom" && preview ? `${preview.from} to ${preview.to}` : "custom dates…"}</option>
          </select>
          <Popover open={openMenu === "custom"} anchor={periodEl} onClose={() => setOpenMenu("")}>
            <div className="report-pop__dates">
              <label><span>From</span><input type="date" value={customFrom} max={customTo || undefined} onChange={(event) => setCustomFrom(event.target.value)} /></label>
              <label><span>To</span><input type="date" value={customTo} min={customFrom || undefined} onChange={(event) => setCustomTo(event.target.value)} /></label>
            </div>
            <div className="report-pop__foot"><button className="button button--primary button--small" type="button" disabled={!customFrom && !customTo} onClick={() => { update({ period: { from: customFrom || undefined, to: customTo || undefined } }); setOpenMenu(""); }}>Apply</button></div>
          </Popover>
        </span>
        <span className="report-anchor report-columns">
          <button ref={setColumnsEl} type="button" className="report-select report-select--button" aria-haspopup="dialog" aria-expanded={openMenu === "columns"} onClick={() => setOpenMenu(openMenu === "columns" ? "" : "columns")}>Columns · {columns.length} of {applicable.length} <ChevronDown /></button>
          <Popover open={openMenu === "columns"} anchor={columnsEl} onClose={() => setOpenMenu("")} align="right">
            <div className="report-pop__tools"><strong className="report-pop__title">Columns in this report</strong><span><button className="admin-link" type="button" onClick={() => setColumns(options?.groups.find((group) => group.key === config.group)?.default_columns || [])}>Defaults</button> · <button className="admin-link" type="button" onClick={() => setColumns(applicable.map((column) => column.key))}>All</button></span></div>
            <ul className="report-columns__list">
              {[...columns, ...applicable.map((column) => column.key).filter((key) => !columns.includes(key))].map((key) => {
                const on = columns.includes(key);
                const index = columns.indexOf(key);
                return <li key={key} className={on ? "is-on" : ""}>
                  <label><input type="checkbox" checked={on} disabled={on && columns.length === 1} onChange={() => toggleColumn(key)} /><span>{labelOf(key)}</span></label>
                  {on && <span className="report-columns__order">
                    <button type="button" aria-label={`Move ${labelOf(key)} up`} disabled={index === 0} onClick={() => moveColumn(key, -1)}>▲</button>
                    <button type="button" aria-label={`Move ${labelOf(key)} down`} disabled={index === columns.length - 1} onClick={() => moveColumn(key, 1)}>▼</button>
                  </span>}
                </li>;
              })}
            </ul>
            <p className="report-pop__hint">Checked columns appear left to right in the order shown. Click a column header in the preview to sort by it.</p>
            <div className="report-pop__foot"><button className="button button--primary button--small" type="button" onClick={() => setOpenMenu("")}>Done</button></div>
          </Popover>
        </span>
        <label className="report-toggle"><input type="checkbox" checked={!config.include_zero} onChange={(event) => update({ include_zero: !event.target.checked })} /> Hide rows with no spend</label>
      </div>

      <div className="report__table admin-table-scroll">
        {previewError && <div className="admin-alert" role="alert">{previewError}</div>}
        <table className={`admin-plain-table report-grid ${loading ? "is-loading" : ""}`}>
          <thead><tr>
            {columns.map((key) => <th key={key} aria-sort={config.sort.key === key ? (config.sort.dir === "asc" ? "ascending" : "descending") : undefined} className={MONEY_COLUMNS.has(key) || key.endsWith("_accounts") || key.endsWith("failures") || key === "days_with_spend" || key === "accounts_with_spend" ? "is-num" : ""}>
              <button type="button" className={`report-th__sort ${config.sort.key === key ? "is-sorted" : ""}`} onClick={() => sortBy(key)} title="Sort by this column">{labelOf(key)}<span aria-hidden="true">{config.sort.key === key ? (config.sort.dir === "asc" ? " ▲" : " ▼") : ""}</span></button>
            </th>)}
          </tr></thead>
          <tbody>
            {(preview?.rows || []).map((row, index) => <tr key={index}>{columns.map((key) => <td key={key} className={typeof row[key] === "number" ? "is-num" : ""}>{formatCell(row[key], key)}</td>)}</tr>)}
            {preview && preview.rows.length === 0 && <tr><td colSpan={columns.length} className="admin-muted report-grid__empty">No rows match. Widen the period, include zero-spend rows, or change the scope.</td></tr>}
            {!preview && !previewError && <tr><td colSpan={columns.length} className="admin-muted report-grid__empty"><LoaderCircle className="spin" /> Building preview…</td></tr>}
          </tbody>
          {totals && <tfoot><tr>{columns.map((key, index) => <td key={key} className={key in totals ? "is-num" : ""}>{index === 0 && !(key in totals) ? "Total" : key in totals ? formatCell(totals[key], key) : ""}</td>)}</tr></tfoot>}
        </table>
      </div>

      <div className="report__foot">
        <span className="admin-muted">{preview ? <>{preview.total.toLocaleString()} row{preview.total === 1 ? "" : "s"} · {columns.length} column{columns.length === 1 ? "" : "s"} · {preview.from} to {preview.to}{preview.total > preview.rows.length ? ` · previewing first ${preview.rows.length}` : ""}</> : "…"}</span>
        <span className="report__buttons">
          <button className="admin-link" type="button" onClick={saveDefault}>{saved ? "Saved" : "Save as my default"}</button>
          <span className="report-anchor">
            <button ref={setMoreEl} className="button button--secondary button--small" type="button" aria-haspopup="menu" aria-expanded={openMenu === "more"} onClick={() => setOpenMenu(openMenu === "more" ? "" : "more")}>More <ChevronDown /></button>
            <Popover open={openMenu === "more"} anchor={moreEl} onClose={() => setOpenMenu("")} align="right">
              <ul className="report-pop__menu" role="menu">
                <li><button type="button" role="menuitem" onClick={() => void copyCsv()}>{copied ? "Copied" : "Copy as CSV (first 50 rows)"}</button></li>
                <li><button type="button" role="menuitem" disabled={Boolean(downloading)} onClick={() => void download("json")}>Download JSON</button></li>
              </ul>
            </Popover>
          </span>
          <button className="button button--primary button--small" type="button" disabled={Boolean(downloading) || !preview || preview.total === 0} onClick={() => void download("csv")}>{downloading === "csv" ? <><LoaderCircle className="spin" /> Preparing…</> : <><Download /> Download CSV</>}</button>
        </span>
      </div>
    </div>
  </div>;
}

function PeriodBar({ period, onChange, range }: { period: Period; onChange: (next: Period) => void; range: { from: string; to: string } | null }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const activeDays = period?.days || (!period ? 30 : undefined);
  return <div className="admin-period" role="group" aria-label="Reporting period">
    {[7, 30, 90].map((days) => <button key={days} type="button" className={activeDays === days ? "is-active" : ""} onClick={() => { setFrom(""); setTo(""); onChange({ days }); }}>{days}d</button>)}
    <span className="admin-period__custom">
      <input type="date" value={from || range?.from || ""} max={to || range?.to || undefined} onChange={(event) => setFrom(event.target.value)} aria-label="From date" />
      <span className="admin-muted">to</span>
      <input type="date" value={to || range?.to || ""} min={from || range?.from || undefined} onChange={(event) => setTo(event.target.value)} aria-label="To date" />
      <button type="button" className="admin-period__apply" disabled={!from && !to} onClick={() => onChange({ from: from || range?.from, to: to || range?.to })}>Apply</button>
    </span>
    {period && !period.days && <span className="admin-status is-active">custom range</span>}
  </div>;
}

type AdminWorkspace = { id: string; name: string; whop_account_id: string; owner_email: string; members: number; created_at: string };
type AdminTotals = { workspaces: number; clients: number; teammates: number; pending_invites: number; new_this_week: number };
type AdminWorkspacesResponse = { totals: AdminTotals; workspaces: AdminWorkspace[] };
type AdminMember = { id: string; email: string; role: "admin" | "member"; status: "invited" | "active"; permissions: string[]; created_at: string };
type AdminWorkspaceDetail = {
  workspace: { id: string; name: string; whop_account_id: string; owner_email: string; owner_user_id: string; owner_account_count: number; created_at: string };
  members: AdminMember[];
  setup: { billingReady: boolean; identityReady: boolean } | null;
  campaigns: { total: number; active: number } | null;
};
type AdminUser = { id: string; email: string; created_at: string | null; last_sign_in_at: string | null; account_count: number; account_names: string[] };
type MetricsResponse = {
  today: string;
  from: string;
  to: string;
  last_synced_date: string | null;
  last_refreshed_at: string | null;
  daily: Array<{ date: string; spend: number }>;
  daily_spenders: Array<{ date: string; accounts: number; clients: number; profit: number }>;
  weekly_spend: Array<{ week_start: string; spend: number; profit: number; days_in_period: number; partial: boolean }>;
  cohorts: Array<{ first_spend: string; size: number; days: Array<number | null> }>;
  month: { spend: number; rate: number; profit: number };
  previous_month: { spend: number; rate: number; profit: number };
  period: { from: string; to: string; days: number; spend: number; profit: number; avg_daily: number; accounts_with_spend: number; previous_spend: number; change_pct: number | null; today_spend: number };
  new_logos_daily: Array<{ date: string; count: number }>;
  new_logos: { count: number; owners: Array<{ name: string; email: string; created_at: string }> };
  churned_logos: { count: number; owners: string[] };
  churned_accounts: Array<{ whop_account_id: string; name: string; email: string; last_spend: string | null; days_quiet: number | null; prior_spend: number; total_spend: number }>;
  nrr_weeks: Array<{ week_start: string; prior_spend: number; retained_spend: number; nrr: number | null }>;
  top_spenders: Array<{
    whop_account_id: string;
    name: string;
    owner_email: string;
    first_1k_spend_date: string | null;
    today_spend: number;
    dod_change_pct: number | null;
    l3_spend: number;
    l3_change_pct: number | null;
    l7_spend: number;
    wow_change_pct: number | null;
    l30_spend: number;
    l30_change_pct: number | null;
    spend_total: number;
    last_spend_date: string | null;
    daily: number[];
    range_spend: number;
  }>;
};

// The metrics response is edge-cached for a couple of minutes, so right after
// a deploy a browser can receive a payload from the previous worker build.
// Fill any series that build did not know about so the page never crashes.
function normalizeMetrics(raw: MetricsResponse): MetricsResponse {
  return {
    ...raw,
    daily: raw.daily ?? [],
    daily_spenders: raw.daily_spenders ?? [],
    weekly_spend: raw.weekly_spend ?? [],
    cohorts: raw.cohorts ?? [],
    churned_accounts: raw.churned_accounts ?? [],
    nrr_weeks: raw.nrr_weeks ?? [],
    top_spenders: raw.top_spenders ?? [],
    new_logos_daily: raw.new_logos_daily ?? [],
    new_logos: raw.new_logos ?? { count: 0, owners: [] },
    churned_logos: raw.churned_logos ?? { count: 0, owners: [] },
  };
}
type FailuresResponse = {
  from: string;
  to: string;
  history: Array<{ id: string; campaign_id: string; account_id: string; account_name: string; owner_email: string; title: string; first_seen_at: string; resolved_at: string | null }>;
  failures: Array<{ campaign_id: string; title: string; status: string; delivery_status: string; updated_at: string | null; account_id: string; account_name: string; owner_email: string }>;
  launch_failures: Array<{ id: string; campaign_id: string; account_id: string; account_name: string; owner_email: string; reason: string; created_at: string }>;
  webhook_events: Array<{ event_id: string; type: string; company_id: string | null; verified: boolean; received_at: string }>;
};

function formatDate(value: string | null) {
  if (!value) return "—";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "—";
  return new Date(parsed).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function money(value: number) {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: value >= 1000 ? 0 : 2 });
}

function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
  return `${Math.round(value * 100) / 100}`;
}

// Full-size bar chart with labeled, gridded axes. Pure SVG, no library.
function Bars({ data, ariaLabel, format, unit, overlay }: { data: Array<{ label: string; value: number }>; ariaLabel: string; format?: (value: number) => string; unit?: string; overlay?: { values: number[]; label: string; format: (value: number) => string } }) {
  const plotWidth = 860;
  const plotHeight = 260;
  const left = 74;
  const bottom = 52;
  const top = 12;
  const max = niceCeiling(Math.max(...data.map((entry) => entry.value), 1));
  const barWidth = plotWidth / Math.max(data.length, 1);
  const describe = format || ((value: number) => String(value));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => max * fraction);
  // At most ~12 axis labels; the last bar is always labeled so the range end is visible.
  const labelStep = Math.max(1, Math.ceil(data.length / 12));
  // The last bar is always labeled so the range end is visible; the regular
  // tick just before it is dropped when it would sit on top of that label.
  const lastIndex = data.length - 1;
  const lastRegular = Math.floor(lastIndex / labelStep) * labelStep;
  const showLabel = (index: number) => index === lastIndex || (index % labelStep === 0 && !(index === lastRegular && lastIndex - lastRegular < labelStep / 2 && lastRegular !== lastIndex));
  const overlayMax = overlay ? niceCeiling(Math.max(...overlay.values, 1)) : 0;
  const right = overlay ? 84 : 16;
  const overlayY = (value: number) => top + plotHeight - (value / overlayMax) * plotHeight;
  return <svg className="admin-chart" viewBox={`0 0 ${left + plotWidth + right} ${top + plotHeight + bottom}`} role="img" aria-label={ariaLabel}>
    {ticks.map((tick) => {
      const y = top + plotHeight - (tick / max) * plotHeight;
      return <g key={tick}>
        <line x1={left} x2={left + plotWidth} y1={y} y2={y} className="admin-chart__grid" />
        <text x={left - 8} y={y + 4} className="admin-chart__tick" textAnchor="end">{unit === "$" ? `$${compactNumber(tick)}` : `${compactNumber(tick)}${unit || ""}`}</text>
      </g>;
    })}
    {overlay && [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
      // Right-hand axis for the overlay line, on its own scale.
      const value = overlayMax * fraction;
      return <text key={`overlay-${fraction}`} x={left + plotWidth + 8} y={overlayY(value) + 4} className="admin-chart__tick admin-chart__tick--line">{overlay.format(value)}</text>;
    })}
    <line x1={left} x2={left} y1={top} y2={top + plotHeight} className="admin-chart__axis" />
    <line x1={left} x2={left + plotWidth} y1={top + plotHeight} y2={top + plotHeight} className="admin-chart__axis" />
    {data.map((entry, index) => {
      const barHeight = entry.value > 0 ? Math.max(3, (entry.value / max) * plotHeight) : 0;
      const x = left + index * barWidth + 2;
      const width = Math.max(barWidth - 4, 2);
      const barTop = top + plotHeight - barHeight;
      return <g key={entry.label}>
        {entry.value > 0 && <rect x={x} y={barTop} width={width} height={barHeight} rx={3} className="admin-chart__bar">
          <title>{`${entry.label}: ${describe(entry.value)}`}</title>
        </rect>}
        {entry.value > 0 && barWidth >= 18 && entry.value >= max * 0.05 && <text x={x + width / 2} y={Math.max(barTop - 6, top + 10)} className="admin-chart__value" textAnchor="middle">{unit === "$" ? `$${compactNumber(entry.value)}` : `${compactNumber(entry.value)}${unit || ""}`}</text>}
        {showLabel(index) && <text x={x + width / 2} y={top + plotHeight + 18} className="admin-chart__label" textAnchor="middle">{entry.label.length > 5 ? entry.label.slice(5) : entry.label}</text>}
      </g>;
    })}
    {overlay && overlay.values.length > 1 && (() => {
      const xAt = (index: number) => left + index * barWidth + barWidth / 2;
      const points = overlay.values.map((value, index) => `${xAt(index)},${overlayY(value)}`).join(" ");
      // Print the running total at the labeled ticks and at the end, always
      // inside the plot so nothing is clipped.
      const marks = overlay.values.map((value, index) => ({ value, index })).filter(({ value, index }) => value > 0 && (index === lastIndex || (index % labelStep === 0 && index !== lastRegular)));
      return <g>
        <polyline points={points} className="admin-chart__line" />
        {marks.map(({ value, index }) => {
          const x = xAt(index);
          const y = overlayY(value);
          const text = overlay.format(value);
          const width = text.length * 7.2 + 10;
          const anchorX = Math.min(Math.max(x, left + width / 2), left + plotWidth - width / 2);
          return <g key={`mark-${index}`}>
            <circle cx={x} cy={y} r={3.2} className="admin-chart__line-dot" />
            <rect x={anchorX - width / 2} y={Math.max(top, y - 24)} width={width} height={17} rx={4} className="admin-chart__line-tag" />
            <text x={anchorX} y={Math.max(top, y - 24) + 12.5} className="admin-chart__line-label" textAnchor="middle">{text}</text>
          </g>;
        })}
      </g>;
    })()}
  </svg>;
}

// Two series over the same dates, as lines, for counts that read better as
// trends than as bars (daily spenders).
function Lines({ data, series, ariaLabel }: { data: string[]; series: Array<{ label: string; values: number[]; className: string }>; ariaLabel: string }) {
  const plotWidth = 860; const plotHeight = 200; const left = 48; const bottom = 40; const top = 14;
  const max = niceCeiling(Math.max(...series.flatMap((entry) => entry.values), 1));
  const step = plotWidth / Math.max(data.length - 1, 1);
  const ticks = [0, 0.5, 1].map((fraction) => Math.round(max * fraction));
  const labelStep = Math.max(1, Math.ceil(data.length / 12));
  return <div className="admin-lines">
    <svg className="admin-chart" viewBox={`0 0 ${left + plotWidth + 16} ${top + plotHeight + bottom}`} role="img" aria-label={ariaLabel}>
      {ticks.map((tick) => { const y = top + plotHeight - (tick / max) * plotHeight; return <g key={tick}><line x1={left} x2={left + plotWidth} y1={y} y2={y} className="admin-chart__grid" /><text x={left - 8} y={y + 4} className="admin-chart__tick" textAnchor="end">{tick}</text></g>; })}
      {series.map((entry) => <polyline key={entry.label} className={`admin-chart__series ${entry.className}`} points={entry.values.map((value, index) => `${left + index * step},${top + plotHeight - (value / max) * plotHeight}`).join(" ")} />)}
      {series.map((entry, seriesIndex) => entry.values.map((value, index) => {
        if (!(index === entry.values.length - 1 || index % labelStep === 0) || value <= 0) return null;
        const x = left + index * step;
        const y = top + plotHeight - (value / max) * plotHeight;
        // First series labels sit above the point, later series below, so
        // two lines close together do not print on top of each other.
        return <g key={`${entry.label}-${index}`}>
          <circle cx={x} cy={y} r={2.6} className={`admin-chart__dot ${entry.className}`} />
          <text x={x} y={seriesIndex === 0 ? y - 7 : y + 14} className={`admin-chart__point ${entry.className}`} textAnchor="middle">{value}</text>
        </g>;
      }))}
      {data.map((date, index) => (index % labelStep === 0 || index === data.length - 1) ? <text key={date} x={left + index * step} y={top + plotHeight + 18} className="admin-chart__label" textAnchor="middle">{date.slice(5)}</text> : null)}
    </svg>
    {(() => {
      // Show the latest day that has data, not a trailing empty day.
      let latest = data.length - 1;
      while (latest > 0 && series.every((entry) => (entry.values[latest] ?? 0) === 0)) latest -= 1;
      return <div className="admin-legend">{series.map((entry) => <span key={entry.label}><i className={entry.className} />{entry.label}: <strong>{entry.values[latest] ?? 0}</strong> on {data[latest]?.slice(5)}</span>)}</div>;
    })()}
  </div>;
}

function Sparkline({ values, tall }: { values: number[]; tall?: boolean }) {
  const max = Math.max(...values, 1);
  const width = tall ? 320 : 260;
  const height = tall ? 130 : 28;
  const barWidth = width / Math.max(values.length, 1);
  return <svg className={`admin-spark ${tall ? "admin-spark--tall" : ""}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
    <line x1={0} x2={width} y1={height - 0.75} y2={height - 0.75} className="admin-chart__baseline" />
    {values.map((value, index) => {
      if (value <= 0) return null;
      const barHeight = Math.max(3, (value / max) * height);
      return <rect key={index} x={index * barWidth} y={height - barHeight} width={Math.max(barWidth - 1.5, 1.5)} height={barHeight} rx={1.5} className="admin-chart__bar" />;
    })}
  </svg>;
}

// Pure: the series ends on the newest date in the data, so rendering
// needs no clock access.
function dailyCounts(dates: Array<string | null | undefined>, days: number): Array<{ label: string; value: number }> {
  const counts = new Map<string, number>();
  let latest = "";
  for (const value of dates) {
    if (!value) continue;
    const day = new Date(value).toISOString().slice(0, 10);
    counts.set(day, (counts.get(day) || 0) + 1);
    if (day > latest) latest = day;
  }
  if (!latest) return [];
  const series: Array<{ label: string; value: number }> = [];
  const end = new Date(`${latest}T00:00:00.000Z`);
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const day = new Date(end);
    day.setUTCDate(day.getUTCDate() - offset);
    const label = day.toISOString().slice(0, 10);
    series.push({ label, value: counts.get(label) || 0 });
  }
  return series;
}

// KPI tile: big number, plain label, and a sparkline only when there is a
// real series behind it (3+ points, not all zero).
function Kpi({ value, label, series, delta, note }: { value: string; label: string; series?: number[]; delta?: number | null; note?: string }) {
  const meaningful = series && series.length >= 3 && series.some((point) => point > 0);
  return <article className="admin-kpi">
    <strong>{value}</strong>
    <span>{label}</span>
    {(delta !== undefined || note) && <small className="admin-kpi__meta">
      {delta !== undefined && delta !== null && <em className={delta >= 0 ? "is-up" : "is-down"}>{delta > 0 ? "+" : ""}{delta.toFixed(1)}% vs prior period</em>}
      {delta === null && <em>no prior-period spend</em>}
      {note && <span>{note}</span>}
    </small>}
    {meaningful && <Sparkline values={series as number[]} />}
  </article>;
}

function OverviewView({ period, refreshKey, onRange, onMeta }: { period: Period; refreshKey: number; onRange: (range: { from: string; to: string }) => void; onMeta: (refreshedAt: string | null) => void }) {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api<MetricsResponse>(withPeriod("/api/admin/metrics", period)).then(normalizeMetrics)
        .then((payload) => { if (!cancelled) { setMetrics(payload); setError(""); onRange({ from: payload.from, to: payload.to }); onMeta(payload.last_refreshed_at); } })
        .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The metrics could not be loaded."); });
    };
    load();
    const poll = window.setInterval(load, 120_000);
    return () => { cancelled = true; window.clearInterval(poll); };
  }, [period, reloadKey, refreshKey, onRange, onMeta]);


  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!metrics) return <div className="admin-state"><LoaderCircle className="spin" /> Loading metrics…</div>;

  const lastFullNrr = [...metrics.nrr_weeks].reverse().find((week) => week.nrr !== null && week.week_start < metrics.today);
  return <>
    <section className="admin-kpis">
      <Kpi value={money(metrics.period.spend)} label={period?.days || !period ? `Spend, last ${metrics.period.days} days` : `Spend, ${metrics.period.from} to ${metrics.period.to}`} delta={metrics.period.change_pct} note={`Today ${money(metrics.period.today_spend)}`} series={metrics.daily.map((day) => day.spend)} />
      <Kpi value={money(metrics.period.profit)} label={period?.days || !period ? `Profit, last ${metrics.period.days} days` : `Profit, ${metrics.period.from} to ${metrics.period.to}`} note={`${money(metrics.period.avg_daily)} avg daily spend`} series={metrics.daily.map((day) => day.spend * metrics.month.rate)} />
      <Kpi value={String(metrics.period.accounts_with_spend)} label="Ad accounts with spend" note={`${money(metrics.month.spend)} spent this calendar month`} />
      <Kpi value={String(metrics.new_logos.count)} label="New clients in period" series={metrics.new_logos_daily.map((day) => day.count)} />
      <Kpi value={String(metrics.churned_logos.count)} label="Churned clients" note="Spent last month, nothing in 14 days" />
      <Kpi value={lastFullNrr?.nrr !== null && lastFullNrr !== undefined ? `${Math.round((lastFullNrr.nrr || 0) * 100)}%` : "n/a"} label="NRR last week" note={lastFullNrr ? `Week of ${lastFullNrr.week_start.slice(5)}` : "Needs a prior week over $100"} series={metrics.nrr_weeks.map((week) => Math.min(300, (week.nrr || 0) * 100))} />
    </section>
    <section className="admin-panel">
      <h2><BarChart3 /> Daily spend</h2>
      <p className="admin-muted">{metrics.from} to {metrics.to}</p>
      <Bars data={metrics.daily.map((day) => ({ label: day.date, value: day.spend }))} ariaLabel="Daily spend for the selected period" format={money} unit="$" />
    </section>
    <section className="admin-panel">
        <h2><Users /> Daily spenders</h2>
        <p className="admin-muted">How many ad accounts spent anything each day, and how many distinct users own those accounts.</p>
        <Lines data={metrics.daily_spenders.map((day) => day.date)} ariaLabel="Ad accounts and clients spending per day" series={[
          { label: "Ad accounts", values: metrics.daily_spenders.map((day) => day.accounts), className: "is-accounts" },
          { label: "Account owners", values: metrics.daily_spenders.map((day) => day.clients), className: "is-clients" },
        ]} />
        <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Day</th><th>Ad accounts</th><th>Account owners</th><th>Spend</th><th>Profit</th></tr></thead><tbody>
          {(() => {
            const spendByDate = new Map(metrics.daily.map((day) => [day.date, day.spend]));
            return [...metrics.daily_spenders].reverse().map((day) => <tr key={day.date}><td>{day.date}</td><td>{day.accounts}</td><td>{day.clients}</td><td>{money(spendByDate.get(day.date) || 0)}</td><td>{money(day.profit)}</td></tr>);
          })()}
        </tbody></table></div>
    </section>
      <section className="admin-panel">
        <h2><BarChart3 /> Weekly spend</h2>
        <p className="admin-muted">Monday-start weeks. A week that only partly falls inside the period is marked.</p>
        <Bars data={metrics.weekly_spend.map((week) => ({ label: `${week.week_start}${week.partial ? "*" : ""}`, value: week.spend }))} ariaLabel="Spend per week" format={money} unit="$" />
        <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Week of</th><th>Days in period</th><th>Spend</th><th>Profit</th></tr></thead><tbody>
          {[...metrics.weekly_spend].reverse().map((week) => <tr key={week.week_start}><td>{week.week_start}{week.partial ? "*" : ""}</td><td>{week.days_in_period}</td><td>{money(week.spend)}</td><td>{money(week.profit)}</td></tr>)}
        </tbody></table></div>
        {metrics.weekly_spend.some((week) => week.partial) && <p className="admin-footnote">* partial week inside the selected period</p>}
      </section>
      <section className="admin-panel">
        <h2><TrendingUp /> Profit</h2>
        <p className="admin-muted">Platform profit per day at that month's rate, with the running total for the period.</p>
        <Bars data={metrics.daily_spenders.map((day) => ({ label: day.date, value: day.profit }))} ariaLabel="Profit per day with cumulative total" format={money} unit="$" overlay={{ label: "Cumulative", format: money, values: metrics.daily_spenders.reduce<number[]>((acc, day) => { acc.push((acc[acc.length - 1] || 0) + day.profit); return acc; }, []) }} />
        <div className="admin-legend"><span><i className="is-bar" />Daily profit (left axis)</span><span><i className="is-line" />Cumulative profit (right axis): <strong>{money(metrics.period.profit)}</strong> for the period</span></div>
        <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Day</th><th>Profit</th><th>Cumulative</th></tr></thead><tbody>
          {(() => {
            const rows = metrics.daily_spenders.reduce<Array<{ date: string; profit: number; cumulative: number }>>((acc, day) => {
              acc.push({ date: day.date, profit: day.profit, cumulative: (acc[acc.length - 1]?.cumulative || 0) + day.profit });
              return acc;
            }, []);
            return rows.reverse().map((row) => <tr key={row.date}><td>{row.date}</td><td>{money(row.profit)}</td><td>{money(row.cumulative)}</td></tr>);
          })()}
        </tbody></table></div>
      </section>
      <section className="admin-panel">
        <h2><CheckCircle2 /> Retention by first-spend day</h2>
        <p className="admin-muted">Ad accounts whose first spend fell in the period, and the share of them still spending on each following day.</p>
        {metrics.cohorts.length === 0 ? <p className="admin-muted">No ad account started spending in this period.</p> : <div className="admin-table-scroll"><table className="admin-plain-table admin-cohorts"><thead><tr><th>First spend</th><th>Accounts</th>{Array.from({ length: 14 }, (_, index) => <th key={index}>D{index + 1}</th>)}</tr></thead><tbody>
          {metrics.cohorts.map((cohort) => <tr key={cohort.first_spend}><td>{cohort.first_spend.slice(5)}</td><td>{cohort.size}</td>{cohort.days.map((value, index) => <td key={index} className={value === null ? "is-future" : ""} style={value === null ? undefined : { background: `rgba(85, 97, 255, ${0.08 + (value / 100) * 0.55})` }}>{value === null ? "" : `${value}%`}</td>)}</tr>)}
        </tbody></table></div>}
      </section>
    <div className="admin-detail__grid">
      <section className="admin-panel">
        <h2><TrendingUp /> Weekly NRR</h2>
        <p className="admin-muted">Spend this week from clients who spent last week, over last week's total. Weeks with under $100 of prior spend are not rated.</p>
        <table className="admin-plain-table"><thead><tr><th>Week of</th><th>Prior spend</th><th>Retained</th><th>NRR</th></tr></thead><tbody>
          {metrics.nrr_weeks.filter((week) => week.prior_spend > 0 || week.retained_spend > 0).map((week) => <tr key={week.week_start}><td>{week.week_start.slice(5)}</td><td>{money(week.prior_spend)}</td><td>{money(week.retained_spend)}</td><td>{week.nrr === null ? <span className="admin-muted" title="Prior week under $100, ratio not meaningful">n/a</span> : `${Math.round(week.nrr * 100)}%`}</td></tr>)}
          {metrics.nrr_weeks.every((week) => week.prior_spend <= 0 && week.retained_spend <= 0) && <tr><td colSpan={4} className="admin-muted">No spend in these weeks.</td></tr>}
        </tbody></table>
      </section>
      <section className="admin-panel">
        <h2><Users /> Logo movement</h2>
        <p className="admin-muted">New: first workspace created inside the selected period. Churned: spent in the prior month, nothing in 14 days.</p>
        <Bars data={metrics.new_logos_daily.map((day) => ({ label: day.date, value: day.count }))} ariaLabel="New logos per day" />
        {metrics.new_logos.owners.length > 0 && <ul className="admin-logos">
          {metrics.new_logos.owners.map((owner) => <li key={`${owner.email}${owner.created_at}`}><span className="admin-status is-active">new</span><strong>{owner.name}</strong><span className="admin-muted">{owner.email || "—"} · {formatDate(owner.created_at)}</span></li>)}
        </ul>}
        {metrics.churned_logos.owners.length > 0 && <ul className="admin-logos">
          {metrics.churned_logos.owners.map((email) => <li key={email}><span className="admin-status is-invited">churned</span><strong>{email}</strong></li>)}
        </ul>}
        {metrics.new_logos.owners.length === 0 && metrics.churned_logos.owners.length === 0 && <p className="admin-muted">No logo movement in this window.</p>}
      </section>
    </div>
    <section className="admin-panel">
      <h2><AlertTriangle /> Churned ad accounts</h2>
      <p className="admin-muted">Ad accounts that spent in the 30 days before the last 14 days of the period and nothing since. Sorted by what they were spending before they went quiet.</p>
      {metrics.churned_accounts.length === 0 ? <p className="admin-muted">No ad account went quiet in this window.</p> : <div className="admin-table-scroll"><table className="admin-plain-table admin-churned"><thead><tr><th>Ad account</th><th>Owner</th><th>Last spend</th><th>Quiet for</th><th>Spend before quiet</th><th>Lifetime spend</th></tr></thead><tbody>
        {metrics.churned_accounts.map((account) => <tr key={account.whop_account_id}>
          <td><strong>{account.name}</strong><div className="admin-muted admin-mono">{account.whop_account_id}</div></td>
          <td>{account.email || <span className="admin-muted">unknown</span>}</td>
          <td>{account.last_spend ? formatDate(`${account.last_spend}T12:00:00Z`) : <span className="admin-muted">never</span>}</td>
          <td>{account.days_quiet === null ? "" : `${account.days_quiet} days`}</td>
          <td>{money(account.prior_spend)}</td>
          <td>{money(account.total_spend)}</td>
        </tr>)}
      </tbody></table></div>}
      {metrics.churned_accounts.length > 0 && <p className="admin-footnote">{metrics.churned_accounts.length} ad account{metrics.churned_accounts.length === 1 ? "" : "s"} across {new Set(metrics.churned_accounts.map((account) => account.email)).size} owner{new Set(metrics.churned_accounts.map((account) => account.email)).size === 1 ? "" : "s"}.</p>}
    </section>
    <p className="admin-footnote">Profit is {`${(metrics.month.rate * 100).toFixed(1)}%`} of spend; the rate moves to 2% in any month where platform spend reaches $10M. Last month: {money(metrics.previous_month.spend)} spend → {money(metrics.previous_month.profit)} profit.</p>
  </>;
}

function Pct({ value }: { value: number | null }) {
  if (value === null) return <span className="admin-muted">—</span>;
  return <span className={value >= 0 ? "admin-pct is-up" : "admin-pct is-down"}>{value > 0 ? "+" : ""}{value.toFixed(1)}%</span>;
}

function SpendersView({ period, refreshKey }: { period: Period; refreshKey: number }) {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [budgets, setBudgets] = useState<Record<string, number | null>>({});
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    api<MetricsResponse>(withPeriod("/api/admin/metrics", period)).then(normalizeMetrics)
      .then((payload) => { if (!cancelled) setMetrics(payload); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The spenders could not be loaded."); });
    api<{ budgets: Record<string, number | null> }>("/api/admin/spender-budgets")
      .then((payload) => { if (!cancelled) setBudgets(payload.budgets); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [period, refreshKey]);
  if (error) return <div className="admin-alert" role="alert">{error}</div>;
  if (!metrics) return <div className="admin-state"><LoaderCircle className="spin" /> Loading top spenders…</div>;
  return <section className="admin-panel">
    <div className="admin-panel__head"><h2><CreditCard /> Top spenders</h2><span className="admin-muted">{metrics.top_spenders.length} clients with spend, ranked by the selected period</span></div>
    <div className="admin-table-scroll">
    <table className="admin-plain-table admin-plain-table--wide"><thead><tr><th>First $1k</th><th>Company</th><th>Account</th><th>Daily budget</th><th>Today</th><th>DoD %</th><th>L3</th><th>L3 %</th><th>L7</th><th>WoW %</th><th>L30</th><th>L30 %</th><th>Range</th><th>Trend</th><th>Owner</th></tr></thead><tbody>
      {metrics.top_spenders.map((entry) => <tr key={entry.whop_account_id}>
        <td>{entry.first_1k_spend_date ? formatDate(entry.first_1k_spend_date) : "—"}</td>
        <td><strong>{entry.name}</strong></td>
        <td className="admin-mono">{entry.whop_account_id}</td>
        <td>{budgets[entry.whop_account_id] != null ? money(budgets[entry.whop_account_id] as number) : "—"}</td>
        <td>{money(entry.today_spend)}</td>
        <td><Pct value={entry.dod_change_pct} /></td>
        <td>{money(entry.l3_spend)}</td>
        <td><Pct value={entry.l3_change_pct} /></td>
        <td>{money(entry.l7_spend)}</td>
        <td><Pct value={entry.wow_change_pct} /></td>
        <td>{money(entry.l30_spend)}</td>
        <td><Pct value={entry.l30_change_pct} /></td>
        <td>{money(entry.range_spend)}</td>
        <td><Sparkline values={entry.daily} /></td>
        <td>{entry.owner_email || "—"}</td>
      </tr>)}
    </tbody></table>
    </div>
    {metrics.top_spenders.length === 0 && <p className="admin-muted">No spend recorded yet. Run a spend sync from Overview.</p>}
  </section>;
}

type ChangeBucket = "paused" | "payment_failed" | "deleted" | "budget_cut" | "delivery" | "growth" | "untracked";
type ChangeBuckets = Record<ChangeBucket, number>;
type SpendChangeResponse = {
  from: string; to: string; peak_day: string; tracking_since: string | null; campaign_history_days: boolean;
  from_total: number; to_total: number; change: number; buckets: ChangeBuckets;
  accounts: Array<{ whop_account_id: string; name: string; from: number; to: number; change: number; buckets: ChangeBuckets }>;
  campaigns: Array<{ campaign_id: string; whop_account_id: string; account_name: string; title: string; from: number; to: number; change: number; bucket: ChangeBucket }>;
  series: Array<{ date: string; total: number; live: number; paused: number; payment_failed: number; deleted: number; untracked: number }>;
  changes: Array<{ id: string; campaign_id: string; account_name: string; title: string; field: "status" | "daily_budget"; old_value: string | null; new_value: string | null; observed_at: string }>;
};

const BUCKET_LABELS: Record<ChangeBucket, string> = {
  paused: "Paused campaigns",
  payment_failed: "Payment failed",
  deleted: "Deleted or not in Whop’s list",
  budget_cut: "Budget cuts",
  delivery: "Live, delivered less",
  growth: "Growth (new or higher spend)",
  untracked: "Not matched to a campaign",
};
const DECLINE_BUCKETS: ChangeBucket[] = ["paused", "payment_failed", "budget_cut", "delivery", "deleted", "untracked"];
const SERIES_KEYS = [
  { key: "live", label: "Live", className: "is-live" },
  { key: "paused", label: "Paused", className: "is-paused" },
  { key: "payment_failed", label: "Payment failed", className: "is-failed" },
  { key: "deleted", label: "Deleted or not listed", className: "is-deleted" },
  { key: "untracked", label: "Not matched", className: "is-untracked" },
] as const;

function signedMoney(value: number) {
  return `${value < 0 ? "−" : value > 0 ? "+" : ""}${money(Math.abs(value))}`;
}

// Daily spend stacked by each campaign's state now, so a drop shows which
// kind of campaign stopped spending.
function StackedBars({ series }: { series: SpendChangeResponse["series"] }) {
  const plotWidth = 860; const plotHeight = 240; const left = 74; const bottom = 40; const top = 12;
  const max = niceCeiling(Math.max(...series.map((day) => SERIES_KEYS.reduce((total, entry) => total + day[entry.key], 0)), 1));
  const barWidth = plotWidth / Math.max(series.length, 1);
  return <svg className="admin-chart" viewBox={`0 0 ${left + plotWidth + 16} ${top + plotHeight + bottom}`} role="img" aria-label="Daily spend by campaign state">
    {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
      const y = top + plotHeight - fraction * plotHeight;
      return <g key={fraction}><line x1={left} x2={left + plotWidth} y1={y} y2={y} className="admin-chart__grid" /><text x={left - 8} y={y + 4} className="admin-chart__tick" textAnchor="end">${compactNumber(max * fraction)}</text></g>;
    })}
    {series.map((day, index) => {
      let stackTop = top + plotHeight;
      const x = left + index * barWidth + 3;
      const width = Math.max(barWidth - 6, 2);
      return <g key={day.date}>
        {SERIES_KEYS.map((entry) => {
          const value = day[entry.key];
          if (value <= 0) return null;
          const height = (value / max) * plotHeight;
          stackTop -= height;
          return <rect key={entry.key} x={x} y={stackTop} width={width} height={Math.max(height, 1)} className={`admin-stack ${entry.className}`}><title>{`${day.date} ${entry.label}: ${money(value)}`}</title></rect>;
        })}
        <text x={x + width / 2} y={top + plotHeight + 18} className="admin-chart__label" textAnchor="middle">{day.date.slice(5)}</text>
      </g>;
    })}
  </svg>;
}

type BudgetHistoryResponse = {
  from: string; to: string; first_day: string; account: string;
  accounts: Array<{ whop_account_id: string; name: string }>;
  series: Array<{ date: string; net_daily_budget: number; live_campaigns: number; pauses: number; launches: number; resumes: number; payment_failed: number; budget_up: number; budget_down: number }>;
  changes: Array<{ id: string; changed_at: string; whop_account_id: string; account_name: string; item_type: string; item_tag: string; campaign_tag: string; campaign_title: string; status_before: string | null; status_after: string | null; budget_before: number | null; budget_after: number | null; budget_type_before: string | null; budget_type_after: string | null; user_tag: string | null; source: string }>;
  change_total: number;
};

// Every campaign's daily budget and status per day since the first campaign,
// rebuilt from Whop's change history plus the 5-minute poll.
function BudgetHistory({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<BudgetHistoryResponse | null>(null);
  const [error, setError] = useState("");
  const [account, setAccount] = useState("");
  const [range, setRange] = useState("all");
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (account) params.set("account", account);
    if (range !== "all") params.set("from", new Date(Date.now() - Number(range) * 86_400_000).toISOString().slice(0, 10));
    api<BudgetHistoryResponse>(`/api/admin/budget-history${params.toString() ? `?${params}` : ""}`)
      .then((payload) => { if (!cancelled) { setError(""); setData(payload); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Budget history could not be loaded."); });
    return () => { cancelled = true; };
  }, [account, range, refreshKey]);

  const describe = (change: BudgetHistoryResponse["changes"][number]) => {
    const parts: string[] = [];
    if (change.status_after) parts.push(`${change.status_before || "?"} → ${change.status_after}`);
    if (change.budget_before !== null || change.budget_after !== null) parts.push(`budget ${change.budget_before === null ? "none" : money(change.budget_before)} → ${change.budget_after === null ? "none" : money(change.budget_after)}`);
    if (change.budget_type_after) parts.push(`budget type ${change.budget_type_before || "?"} → ${change.budget_type_after}`);
    return parts.join(", ");
  };
  return <section className="admin-panel">
    <h2><TrendingDown /> Budget history</h2>
    <p className="admin-muted">Total daily budget on live campaigns each day, with every status and budget change behind it. Rebuilt from Whop’s own change history since the first campaign, then kept current every 5 minutes.</p>
    <div className="admin-change-pick">
      <label>Ad account <select value={account} onChange={(event) => setAccount(event.target.value)}><option value="">All ad accounts</option>{(data?.accounts || []).map((entry) => <option key={entry.whop_account_id} value={entry.whop_account_id}>{entry.name}</option>)}</select></label>
      <label>Range <select value={range} onChange={(event) => setRange(event.target.value)}><option value="all">Since the first campaign</option><option value="90">Last 90 days</option><option value="30">Last 30 days</option><option value="14">Last 14 days</option></select></label>
    </div>
    {error && <div className="admin-alert" role="alert">{error}</div>}
    {!data && !error && <div className="admin-state"><LoaderCircle className="spin" /> Loading budget history…</div>}
    {data && <>
      <Bars data={data.series.map((day) => ({ label: day.date, value: day.net_daily_budget }))} ariaLabel="Total daily budget on live campaigns per day" format={money} unit="$" overlay={{ label: "Live campaigns", format: (value) => String(Math.round(value)), values: data.series.map((day) => day.live_campaigns) }} />
      <div className="admin-legend"><span><i className="is-bar" />Daily budget on live campaigns (left axis)</span><span><i className="is-line" />Live campaigns (right axis)</span></div>
      <Lines data={data.series.map((day) => day.date)} ariaLabel="Campaigns paused and launched per day" series={[
        { label: "Paused", values: data.series.map((day) => day.pauses), className: "is-clients" },
        { label: "Launched or resumed", values: data.series.map((day) => day.launches + day.resumes), className: "is-accounts" },
      ]} />
      <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Day</th><th>Daily budget</th><th>Live campaigns</th><th>Paused</th><th>Launched</th><th>Resumed</th><th>Payment failed</th><th>Budget raised</th><th>Budget cut</th></tr></thead><tbody>
        {[...data.series].reverse().map((day) => <tr key={day.date}><td>{day.date}</td><td>{money(day.net_daily_budget)}</td><td>{day.live_campaigns}</td><td>{day.pauses}</td><td>{day.launches}</td><td>{day.resumes}</td><td>{day.payment_failed}</td><td>{day.budget_up}</td><td>{day.budget_down}</td></tr>)}
      </tbody></table></div>
      <h3 className="admin-subhead">Changes ({data.change_total.toLocaleString()}{data.change_total > data.changes.length ? `, newest ${data.changes.length} shown` : ""})</h3>
      <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table"><thead><tr><th>When (UTC)</th><th>Ad account</th><th>Campaign</th><th>Item</th><th>Change</th><th>By</th></tr></thead><tbody>
        {data.changes.map((change) => <tr key={change.id}><td>{change.changed_at.slice(0, 16).replace("T", " ")}</td><td>{change.account_name}</td><td title={change.campaign_tag}>{change.campaign_title || change.campaign_tag}</td><td>{change.item_type === "AdGroup" ? `Ad group ${change.item_tag}` : "Campaign"}</td><td>{describe(change)}</td><td>{change.user_tag || (change.source === "poll" ? "Seen by the 5-minute sync" : "No user recorded")}</td></tr>)}
      </tbody></table></div>
      <p className="admin-footnote">A campaign’s daily budget is its own daily budget, or the sum of its active ad groups’ daily budgets when it has none. Lifetime budgets are not counted. “No user recorded” means Whop logged the change without a person attached, for example through the API.</p>
    </>}
  </section>;
}

function SpendChangeView({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<SpendChangeResponse | null>(null);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    let cancelled = false;
    api<SpendChangeResponse>(`/api/admin/spend-change${query}`)
      .then((payload) => { if (!cancelled) { setError(""); setData(payload); setFrom(payload.from); setTo(payload.to); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "Spend change could not be loaded."); });
    return () => { cancelled = true; };
  }, [query, refreshKey]);

  if (error) return <div className="admin-alert" role="alert">{error}</div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Loading spend change…</div>;
  const decline = DECLINE_BUCKETS.reduce((total, key) => total + Math.min(0, data.buckets[key]), 0);
  const share = (value: number) => (decline < 0 && value < 0 ? `${Math.round((value / decline) * 100)}%` : "");
  const pct = data.from_total > 0 ? Math.round((data.change / data.from_total) * 100) : null;
  const changeLabel = (change: SpendChangeResponse["changes"][number]) => change.field === "daily_budget"
    ? `Daily budget ${change.old_value === null ? "none" : money(Number(change.old_value))} → ${change.new_value === null ? "none" : money(Number(change.new_value))}`
    : `Status ${change.old_value || "?"} → ${change.new_value || "?"}`;

  return <>
    <BudgetHistory refreshKey={refreshKey} />
    <section className="admin-panel">
      <h2><TrendingDown /> Spend change</h2>
      <p className="admin-muted">Compares each campaign’s spend on two days and sorts the difference by why it moved, judged by the campaign’s status at the end of the later day.</p>
      <div className="admin-change-pick">
        <label>From <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>To <input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <button className="button button--secondary button--small" type="button" onClick={() => setQuery(`?from=${from}&to=${to}`)}>Compare</button>
        <span className="admin-muted">Defaults to the highest-spend day of the prior week ({data.peak_day}) against yesterday.</span>
      </div>
      <p className="admin-change-headline">Spend went from <strong>{money(data.from_total)}</strong> on {data.from} to <strong>{money(data.to_total)}</strong> on {data.to}: <strong>{signedMoney(data.change)}</strong>{pct !== null ? ` (${pct}%)` : ""}.</p>
      <div className="admin-table-scroll"><table className="admin-plain-table admin-numbers"><thead><tr><th>Cause</th><th>Change</th><th>Share of the decline</th></tr></thead><tbody>
        {[...DECLINE_BUCKETS, "growth" as ChangeBucket].filter((key) => Math.abs(data.buckets[key]) >= 0.01).map((key) => <tr key={key}><td>{BUCKET_LABELS[key]}</td><td>{signedMoney(data.buckets[key])}</td><td>{key === "growth" ? "offsets the decline" : share(data.buckets[key])}</td></tr>)}
      </tbody></table></div>
      <p className="admin-footnote">Paused includes campaigns whose ad groups are all paused. A budget cut is a lower daily budget at the end of the later day than at the end of the earlier one. “Not matched” is spend Whop reports for the account that no listed campaign accounts for.</p>
    </section>
    <section className="admin-panel">
      <h2><BarChart3 /> Daily spend by campaign state</h2>
      <p className="admin-muted">The last 14 days to {data.to}, each campaign colored by its status at the end of {data.to}.</p>
      {data.campaign_history_days ? <StackedBars series={data.series} /> : <p className="admin-muted">Per-campaign history is still being filled. It appears within the hour.</p>}
      <div className="admin-legend">{SERIES_KEYS.map((entry) => <span key={entry.key}><i className={`admin-stack ${entry.className}`} />{entry.label}</span>)}</div>
    </section>
    <section className="admin-panel">
      <h2><Building2 /> By ad account</h2>
      <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Ad account</th><th>{data.from}</th><th>{data.to}</th><th>Change</th><th>Paused</th><th>Payment failed</th><th>Budget cuts</th><th>Delivered less</th><th>Deleted / not matched</th><th>Growth</th></tr></thead><tbody>
        {data.accounts.map((account) => <tr key={account.whop_account_id}><td>{account.name}</td><td>{money(account.from)}</td><td>{money(account.to)}</td><td>{signedMoney(account.change)}</td><td>{signedMoney(account.buckets.paused)}</td><td>{signedMoney(account.buckets.payment_failed)}</td><td>{signedMoney(account.buckets.budget_cut)}</td><td>{signedMoney(account.buckets.delivery)}</td><td>{signedMoney(account.buckets.deleted + account.buckets.untracked)}</td><td>{signedMoney(account.buckets.growth)}</td></tr>)}
      </tbody></table></div>
    </section>
    <section className="admin-panel">
      <h2><TrendingDown /> Biggest campaign moves</h2>
      <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table admin-numbers"><thead><tr><th>Ad account</th><th>Campaign</th><th>{data.from}</th><th>{data.to}</th><th>Change</th><th>Cause</th></tr></thead><tbody>
        {data.campaigns.slice(0, 100).map((campaign) => <tr key={campaign.campaign_id}><td>{campaign.account_name}</td><td title={campaign.campaign_id}>{campaign.title || campaign.campaign_id}</td><td>{money(campaign.from)}</td><td>{money(campaign.to)}</td><td>{signedMoney(campaign.change)}</td><td>{BUCKET_LABELS[campaign.bucket]}</td></tr>)}
      </tbody></table></div>
    </section>
    <section className="admin-panel">
      <h2><RefreshCw /> Budget and status changes</h2>
      <p className="admin-muted">{data.tracking_since ? `Recorded every 5 minutes since ${formatDate(data.tracking_since)}.` : "Recording starts with the next sync."} Changes before that are not available here.</p>
      {data.changes.length === 0 ? <p className="admin-muted">No changes recorded between {data.from} and {data.to}.</p> : <div className="admin-table-scroll admin-table-scroll--capped"><table className="admin-plain-table"><thead><tr><th>Seen</th><th>Ad account</th><th>Campaign</th><th>Change</th></tr></thead><tbody>
        {data.changes.map((change) => <tr key={change.id}><td>{formatDate(change.observed_at)}</td><td>{change.account_name}</td><td title={change.campaign_id}>{change.title || change.campaign_id}</td><td>{changeLabel(change)}</td></tr>)}
      </tbody></table></div>}
    </section>
  </>;
}

function FailuresView({ period }: { period: Period }) {
  const [data, setData] = useState<FailuresResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<FailuresResponse>(withPeriod("/api/admin/payment-failures", period))
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The payment failures could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey, period]);

  const retry = async (campaignId: string, accountId: string) => {
    setBusy(campaignId);
    setError("");
    setNotice("");
    try {
      await api(`/api/admin/campaigns/${encodeURIComponent(campaignId)}/retry-payment`, { method: "POST", body: JSON.stringify({ account_id: accountId }) });
      setNotice("Payment retry requested. The charge re-attempts shortly.");
      setReloadKey((value) => value + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The payment retry failed."); }
    finally { setBusy(""); }
  };

  if (error && !data) return <div className="admin-alert" role="alert">{error}</div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Scanning campaigns for payment failures…</div>;

  return <>
    {error && <div className="admin-alert" role="alert">{error}</div>}
    {notice && <div className="admin-notice" role="status">{notice}</div>}
    <section className="admin-panel">
      <h2><AlertTriangle /> Campaigns with failed spend charges</h2>
      <p className="admin-muted">Admins are emailed when a campaign's spend charge fails. Retry the charge here once the client updates their card.</p>
      {data.failures.length > 0 && <Bars data={dailyCounts(data.failures.map((failure) => failure.updated_at), 30)} ariaLabel="Failed campaigns by last update day" />}
      {data.failures.length === 0 && <p className="admin-ok"><CheckCircle2 /> No campaigns are in payment-failed state right now.</p>}
      {data.failures.length > 0 && <table className="admin-plain-table"><thead><tr><th>Campaign</th><th>Client</th><th>Owner</th><th>Last update</th><th /></tr></thead><tbody>
        {data.failures.map((failure) => <tr key={failure.campaign_id}>
          <td><strong>{failure.title}</strong><br /><span className="admin-mono">{failure.campaign_id}</span></td>
          <td>{failure.account_name}</td>
          <td>{failure.owner_email || "—"}</td>
          <td>{formatDate(failure.updated_at)}</td>
          <td><button className="button button--secondary button--small" type="button" disabled={busy === failure.campaign_id} onClick={() => void retry(failure.campaign_id, failure.account_id)}>{busy === failure.campaign_id ? "Retrying…" : "Retry charge"}</button></td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><CreditCard /> Payment failure history</h2>
      <p className="admin-muted">Every recorded spend-charge failure in the selected period, including recovered ones.</p>
      {data.history.length > 0 && <Bars data={dailyCounts(data.history.map((entry) => entry.first_seen_at), 30)} ariaLabel="Payment failures per day" />}
      {data.history.length === 0 && <p className="admin-muted">No payment failures recorded in this period.</p>}
      {data.history.length > 0 && <table className="admin-plain-table"><thead><tr><th>First seen</th><th>Status</th><th>Client</th><th>Owner</th><th>Campaign</th></tr></thead><tbody>
        {data.history.map((entry) => <tr key={entry.id}>
          <td>{formatDate(entry.first_seen_at)}</td>
          <td>{entry.resolved_at ? <span className="admin-status is-active">recovered {formatDate(entry.resolved_at)}</span> : <span className="admin-status is-invited">ongoing</span>}</td>
          <td>{entry.account_name}</td>
          <td>{entry.owner_email || "—"}</td>
          <td className="admin-mono">{entry.campaign_id}</td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><XCircle /> Campaign launch rejections</h2>
      <p className="admin-muted">Every launch the ad network declined, with the exact reason. Kept permanently.</p>
      {data.launch_failures.length > 0 && <Bars data={dailyCounts(data.launch_failures.map((failure) => failure.created_at), 30)} ariaLabel="Launch rejections per day" />}
      {data.launch_failures.length === 0 && <p className="admin-muted">No launch rejections recorded yet.</p>}
      {data.launch_failures.length > 0 && <table className="admin-plain-table"><thead><tr><th>When</th><th>Client</th><th>Owner</th><th>Campaign</th><th>Reason</th></tr></thead><tbody>
        {data.launch_failures.map((failure) => <tr key={failure.id}>
          <td>{formatDate(failure.created_at)} {new Date(failure.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</td>
          <td>{failure.account_name}</td>
          <td>{failure.owner_email || "—"}</td>
          <td className="admin-mono">{failure.campaign_id}</td>
          <td>{failure.reason || "—"}</td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><Mail /> Payment webhooks</h2>
      <p className="admin-muted">Payment events delivered to this platform's webhook endpoint.</p>
      {data.webhook_events.length > 0 && <Bars data={dailyCounts(data.webhook_events.map((event) => event.received_at), 30)} ariaLabel="Payment webhook deliveries per day" />}
      {data.webhook_events.length === 0 && <p className="admin-muted">No payment webhook deliveries received yet.</p>}
      {data.webhook_events.length > 0 && <table className="admin-plain-table"><thead><tr><th>Type</th><th>Company</th><th>Verified</th><th>Received</th></tr></thead><tbody>
        {data.webhook_events.map((event) => <tr key={event.event_id}><td>{event.type}</td><td className="admin-mono">{event.company_id || "—"}</td><td>{event.verified ? "yes" : "no"}</td><td>{formatDate(event.received_at)}</td></tr>)}
      </tbody></table>}
    </section>
  </>;
}

type UserSortKey = "email" | "accounts" | "created" | "seen";

// Requests tab: every new ad account is a request an admin approves. Pending
// queue first, then the decision history.
function UsersView() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState("");
  const [sort, setSort] = useState<{ key: UserSortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const toggleSort = (key: UserSortKey) => setSort((current) => current.key === key
    ? { key, dir: current.dir === 1 ? -1 : 1 }
    : { key, dir: key === "email" ? 1 : -1 });

  useEffect(() => {
    let cancelled = false;
    api<{ users: AdminUser[] }>("/api/admin/users")
      .then((payload) => { if (!cancelled) { setUsers(payload.users); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The users could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [pauseFirst, setPauseFirst] = useState(true);
  const removeUser = async () => {
    if (!removing) return;
    setBusy(`remove:${removing.id}`);
    setError("");
    setNotice("");
    try {
      const result = await api<{ campaigns_paused: number; ad_accounts_detached: number }>(`/api/admin/users/${encodeURIComponent(removing.id)}`, { method: "DELETE", body: JSON.stringify({ confirm_email: confirmEmail, pause_campaigns: pauseFirst }) });
      setNotice(`${removing.email} was removed. ${result.campaigns_paused} campaign${result.campaigns_paused === 1 ? "" : "s"} paused, ${result.ad_accounts_detached} ad account${result.ad_accounts_detached === 1 ? "" : "s"} detached.`);
      setRemoving(null);
      setConfirmEmail("");
      setReloadKey((value) => value + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The user could not be removed."); }
    finally { setBusy(""); }
  };

  const viewAsUser = async (user: AdminUser) => {
    setBusy(`view:${user.id}`);
    setError("");
    try {
      const grant = await api<{ token: string }>("/api/admin/impersonate-user", { method: "POST", body: JSON.stringify({ user_id: user.id }) });
      window.open(`/app?imp=${encodeURIComponent(grant.token)}`, "_blank", "noopener");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The user session could not be opened."); }
    finally { setBusy(""); }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? users.filter((user) => user.email.includes(needle) || user.account_names.some((name) => name.toLowerCase().includes(needle)))
      : users;
    const rank = (user: AdminUser): string | number => {
      if (sort.key === "email") return user.email;
      if (sort.key === "accounts") return user.account_count;
      if (sort.key === "created") return user.created_at || "";
      return user.last_sign_in_at || "";
    };
    return [...matched].sort((a, b) => {
      const left = rank(a);
      const right = rank(b);
      const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      return order * sort.dir;
    });
  }, [users, query, sort]);

  // Matching account names float to the front of the preview so a search hit
  // is visible even when the user has more accounts than the row can show.
  const namesPreview = (user: AdminUser) => {
    const needle = query.trim().toLowerCase();
    const names = needle
      ? [...user.account_names].sort((a, b) => Number(b.toLowerCase().includes(needle)) - Number(a.toLowerCase().includes(needle)))
      : user.account_names;
    return `${names.slice(0, 3).join(", ")}${names.length > 3 ? ` +${names.length - 3} more` : ""}`;
  };

  const sortHeader = (label: string, key: UserSortKey) => (
    <th aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}>
      <button type="button" className={sort.key === key ? "is-sorted" : ""} onClick={() => toggleSort(key)}>
        {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );

  if (error && !users) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!users) return <div className="admin-state"><LoaderCircle className="spin" /> Loading users…</div>;

  return <>
    {error && <div className="admin-alert" role="alert">{error}</div>}
    {notice && <div className="admin-notice" role="status">{notice}</div>}
    {removing && <div className="admin-modal-backdrop" role="presentation" onClick={() => setRemoving(null)}>
      <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="remove-user-title" onClick={(event) => event.stopPropagation()}>
        <h3 id="remove-user-title"><UserMinus /> Remove {removing.email}</h3>
        <p>This deletes their sign-in. Their {removing.account_count} ad account{removing.account_count === 1 ? "" : "s"}{removing.account_names.length > 0 ? ` (${removing.account_names.slice(0, 4).join(", ")}${removing.account_names.length > 4 ? ", …" : ""})` : ""} disappear from the portal but keep existing upstream, and any active campaign keeps spending on the card on file unless paused.</p>
        <label className="admin-modal__check"><input type="checkbox" checked={pauseFirst} onChange={(event) => setPauseFirst(event.target.checked)} /> Pause all of their active campaigns first (recommended)</label>
        <label className="admin-modal__field"><span>Type <strong>{removing.email}</strong> to confirm</span><input value={confirmEmail} autoComplete="off" onChange={(event) => setConfirmEmail(event.target.value)} /></label>
        <div className="admin-modal__actions">
          <button className="button button--secondary button--small" type="button" onClick={() => setRemoving(null)}>Cancel</button>
          <button className="button button--primary button--small admin-danger-solid" type="button" disabled={busy === `remove:${removing.id}` || confirmEmail.trim().toLowerCase() !== removing.email} onClick={() => void removeUser()}>{busy === `remove:${removing.id}` ? "Removing…" : "Remove user"}</button>
        </div>
      </div>
    </div>}
    <section className="admin-panel">
      <div className="admin-panel__head">
        <div><h2><Users /> Users</h2><span className="admin-muted">{users.length} users, {users.reduce((sum, user) => sum + user.account_count, 0)} ad accounts.</span></div>
        <div className="admin-panel__tools">
          <label className="admin-inline-search"><Search /><input value={query} placeholder="Search email or account" onChange={(event) => setQuery(event.target.value)} /></label>
        </div>
      </div>
      <div className="admin-table-scroll">
      <table className="admin-plain-table admin-users-table"><thead><tr>{sortHeader("Email", "email")}{sortHeader("Ad accounts", "accounts")}{sortHeader("Signed up", "created")}{sortHeader("Last sign-in", "seen")}<th /></tr></thead><tbody>
        {filtered.map((user) => <tr key={user.id}>
          <td><strong>{user.email || "—"}</strong></td>
          <td title={user.account_names.join(", ")}>{user.account_count > 0 ? <>{user.account_count} <span className="admin-muted">· {namesPreview(user)}</span></> : <span className="admin-muted">none yet</span>}</td>
          <td>{formatDate(user.created_at)}</td>
          <td>{formatDate(user.last_sign_in_at)}</td>
          <td className="admin-actions"><button className="button button--primary button--small" type="button" disabled={busy === `view:${user.id}` || user.account_count === 0} onClick={() => void viewAsUser(user)}>{busy === `view:${user.id}` ? "Opening…" : "View as user"}</button><button className="button button--secondary button--small admin-danger" type="button" disabled={busy === `remove:${user.id}`} onClick={() => { setRemoving(user); setConfirmEmail(""); setPauseFirst(true); }} aria-label={`Remove ${user.email}`} title="Remove this user">{busy === `remove:${user.id}` ? <LoaderCircle className="spin" /> : <UserMinus />}</button></td>
        </tr>)}
      </tbody></table>
      </div>
      {filtered.length === 0 && <p className="admin-muted">No users match that search.</p>}
    </section>
  </>;
}

type WorkspaceSortKey = "name" | "owner" | "members" | "created";

function WorkspacesView() {
  const [data, setData] = useState<AdminWorkspacesResponse | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: WorkspaceSortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const toggleSort = (key: WorkspaceSortKey) => setSort((current) => current.key === key
    ? { key, dir: current.dir === 1 ? -1 : 1 }
    : { key, dir: key === "name" || key === "owner" ? 1 : -1 });
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminWorkspaceDetail | null>(null);
  const [detailError, setDetailError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<AdminWorkspacesResponse>("/api/admin/workspaces")
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The ad accounts could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const openWorkspace = (id: string | null) => {
    setSelected(id);
    setDetail(null);
    setDetailError("");
  };

  const [manageBusy, setManageBusy] = useState("");
  const manage = async (id: string) => {
    setManageBusy(id);
    try {
      const grant = await api<{ token: string }>("/api/admin/impersonate", { method: "POST", body: JSON.stringify({ workspace_id: id }) });
      window.open(`/app?imp=${encodeURIComponent(grant.token)}`, "_blank", "noopener");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The managed session could not be opened."); }
    finally { setManageBusy(""); }
  };

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    api<AdminWorkspaceDetail>(`/api/admin/workspaces/${encodeURIComponent(selected)}`)
      .then((payload) => { if (!cancelled) setDetail(payload); })
      .catch((caught) => { if (!cancelled) setDetailError(caught instanceof Error ? caught.message : "The ad account could not be loaded."); });
    return () => { cancelled = true; };
  }, [selected]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? data.workspaces.filter((workspace) =>
          workspace.name.toLowerCase().includes(needle)
          || workspace.owner_email.includes(needle)
          || workspace.whop_account_id.toLowerCase().includes(needle))
      : data.workspaces;
    const rank = (workspace: AdminWorkspace): string | number => {
      if (sort.key === "name") return workspace.name.toLowerCase();
      if (sort.key === "owner") return workspace.owner_email;
      if (sort.key === "members") return workspace.members;
      return workspace.created_at || "";
    };
    return [...matched].sort((a, b) => {
      const left = rank(a);
      const right = rank(b);
      const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      return order * sort.dir;
    });
  }, [data, query, sort]);

  const sortHeader = (label: string, key: WorkspaceSortKey) => (
    <button type="button" className={`admin-sort ${sort.key === key ? "is-sorted" : ""}`} onClick={() => toggleSort(key)}>
      {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
    </button>
  );

  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Loading ad accounts…</div>;

  return <>
    <section className="admin-kpis admin-kpis--auto">
      <article className="admin-kpi"><strong>{data.totals.workspaces}</strong><span>Ad accounts</span></article>
      <article className="admin-kpi"><strong>{data.totals.clients}</strong><span>Clients</span></article>
      <article className="admin-kpi"><strong>{data.totals.teammates}</strong><span>Teammates</span></article>
      <article className="admin-kpi"><strong>{data.totals.pending_invites}</strong><span>Pending invites</span></article>
      <article className="admin-kpi"><strong>{data.totals.new_this_week}</strong><span>New this week</span></article>
    </section>

    {!selected && <>
      <section className="admin-panel"><h2><Building2 /> New ad accounts per day</h2><Bars data={dailyCounts(data.workspaces.map((workspace) => workspace.created_at), 30)} ariaLabel="New ad accounts per day" /></section>
      <div className="admin-search"><Search /><input value={query} placeholder="Search by business, owner email, or account id" onChange={(event) => setQuery(event.target.value)} /></div>
      <section className="admin-table" role="table" aria-label="Client ad accounts">
        <header role="row"><span>{sortHeader("Business", "name")}</span><span>{sortHeader("Owner", "owner")}</span><span>{sortHeader("Members", "members")}</span><span>Account</span><span>{sortHeader("Created", "created")}</span></header>
        {filtered.map((workspace) => (
          <button type="button" key={workspace.id} role="row" onClick={() => openWorkspace(workspace.id)}>
            <span><Building2 />{workspace.name}</span>
            <span>{workspace.owner_email || "—"}</span>
            <span>{workspace.members}</span>
            <span className="admin-mono">{workspace.whop_account_id}</span>
            <span>{formatDate(workspace.created_at)}</span>
          </button>
        ))}
        {filtered.length === 0 && <div className="admin-empty">No ad accounts match that search.</div>}
      </section>
    </>}

    {selected && <section className="admin-detail">
      <button className="admin-back" type="button" onClick={() => openWorkspace(null)}><ArrowLeft /> All ad accounts</button>
      {detailError && <div className="admin-alert" role="alert">{detailError}</div>}
      {!detail && !detailError && <div className="admin-state"><LoaderCircle className="spin" /> Loading ad account…</div>}
      {detail && <>
        <div className="admin-detail__head">
          <div><strong>{detail.workspace.name}</strong><span><Mail /> {detail.workspace.owner_email || "owner unknown"}</span></div>
          <div className="admin-detail__head-actions">
            <span className="admin-mono">{detail.workspace.whop_account_id}</span>
            <button className="button button--primary button--small" type="button" disabled={manageBusy === detail.workspace.id} onClick={() => void manage(detail.workspace.id)}>{manageBusy === detail.workspace.id ? "Opening…" : "Manage ad account"}</button>
          </div>
        </div>
        <div className="admin-detail__grid">
          <article className="admin-panel">
            <h2>Setup</h2>
            {detail.setup ? <ul className="admin-checks">
              <li>{detail.setup.billingReady ? <CheckCircle2 className="is-ok" /> : <XCircle className="is-bad" />} Billing card connected</li>
              <li>{detail.setup.identityReady ? <CheckCircle2 className="is-ok" /> : <XCircle className="is-bad" />} Identity ready</li>
            </ul> : <p className="admin-muted">Live setup state is unavailable right now.</p>}
            <h2>Campaigns</h2>
            {detail.campaigns ? <p>{detail.campaigns.active} active of {detail.campaigns.total} recent campaigns</p> : <p className="admin-muted">Campaign data is unavailable right now.</p>}
            <h2>Created</h2>
            <p>{formatDate(detail.workspace.created_at)}</p>
            <h2>Owner limit</h2>
            <p>The owner has {detail.workspace.owner_account_count} ad account{detail.workspace.owner_account_count === 1 ? "" : "s"}.</p>
          </article>
          <article className="admin-panel">
            <h2><Users /> Team ({detail.members.length + 1})</h2>
            <ul className="admin-members">
              <li><strong>{detail.workspace.owner_email || "Owner"}</strong><span className="admin-role">owner</span><span className="admin-status is-active">active</span></li>
              {detail.members.map((member) => (
                <li key={member.id}><strong>{member.email}</strong><span className="admin-role">{member.role}</span><span className={`admin-status ${member.status === "active" ? "is-active" : "is-invited"}`}>{member.status}</span></li>
              ))}
            </ul>
          </article>
        </div>
      </>}
    </section>}
  </>;
}

type PlatformAdminEntry = { id: string | null; email: string; source: "config" | "added"; added_by: string | null; created_at: string | null };

function AdminsView() {
  const [admins, setAdmins] = useState<PlatformAdminEntry[] | null>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<{ admins: PlatformAdminEntry[] }>("/api/admin/admins")
      .then((payload) => { if (!cancelled) setAdmins(payload.admins); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The administrators could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const add = async () => {
    const value = email.trim().toLowerCase();
    if (!value.includes("@")) { setError("Enter a valid email address."); return; }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api("/api/admin/admins", { method: "POST", body: JSON.stringify({ email: value }) });
      setEmail("");
      setNotice(`${value} can now open the admin dashboard on this platform.`);
      setReloadKey((current) => current + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The administrator could not be added."); }
    finally { setBusy(false); }
  };

  const remove = async (entry: PlatformAdminEntry) => {
    if (!entry.id) return;
    if (!window.confirm(`Remove ${entry.email} from the platform administrators?`)) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(`/api/admin/admins/${encodeURIComponent(entry.id)}`, { method: "DELETE" });
      setReloadKey((current) => current + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The administrator could not be removed."); }
    finally { setBusy(false); }
  };

  return <section className="team-workspace">
    <div className="team-invite">
      <div><strong>Administrators</strong><span>Admins can open the admin dashboard and manage this list. Built-in admins come from the platform configuration.</span></div>
      <div className="team-invite__form">
        <input value={email} type="email" placeholder="teammate@company.com" onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void add(); }} />
        <button className="button button--primary button--small" type="button" disabled={busy} onClick={() => void add()}>{busy ? "Saving…" : "Add admin"}</button>
      </div>
    </div>
    {error && <p className="team-error" role="alert">{error}</p>}
    {notice && <p className="team-notice" role="status">{notice}</p>}
    {!admins && !error && <div className="team-loading">Loading administrators…</div>}
    {admins && <div className="team-list">
      {admins.map((entry) => <article key={entry.id || entry.email}>
        <div className="team-list__top">
          <div className="team-list__who"><strong>{entry.email}</strong><span>{entry.source === "config" ? "Built-in, set in the platform configuration" : `Added${entry.added_by ? ` by ${entry.added_by}` : ""}`}</span></div>
          {entry.source === "added"
            ? <button className="team-list__remove" type="button" disabled={busy} onClick={() => void remove(entry)} aria-label={`Remove ${entry.email}`}><Trash2 /></button>
            : <span className="admin-builtin"><ShieldCheck /> Built-in</span>}
        </div>
      </article>)}
    </div>}
  </section>;
}

type WebhookEventsResponse = {
  types: string[];
  events: Array<{ event_id: string; type: string; company_id: string | null; verified: boolean; received_at: string; payload: unknown }>;
};

function EventsView({ period }: { period: Period }) {
  const [data, setData] = useState<WebhookEventsResponse | null>(null);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<WebhookEventsResponse>(withPeriod("/api/admin/webhook-events", period, typeFilter ? `type=${encodeURIComponent(typeFilter)}` : ""))
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The events could not be loaded."); });
    return () => { cancelled = true; };
  }, [typeFilter, reloadKey, period]);

  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Loading events…</div>;

  return <section className="admin-panel">
    <div className="admin-toolbar">
      <h2 style={{ margin: 0 }}><Webhook /> Webhook events</h2>
      <div className="admin-toolbar__controls">
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by event type">
          <option value="">All types</option>
          {data.types.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
        <button className="button button--secondary button--small" type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Refresh</button>
      </div>
    </div>
    <p className="admin-muted">Every delivery to /api/webhooks/whop, newest first. Unverified means no webhook secret is configured yet or the signature did not match.</p>
    {data.events.length > 0 && <Bars data={dailyCounts(data.events.map((event) => event.received_at), 30)} ariaLabel="Webhook deliveries per day" />}
    {data.events.length === 0 && <p className="admin-muted">No deliveries stored yet.</p>}
    {data.events.map((event) => <details className="admin-event" key={event.event_id}>
      <summary>
        <span className="admin-event__type">{event.type}</span>
        <span className="admin-mono">{event.company_id || "—"}</span>
        <span className={`admin-status ${event.verified ? "is-active" : "is-invited"}`}>{event.verified ? "verified" : "unverified"}</span>
        <span className="admin-muted">{formatDate(event.received_at)} {new Date(event.received_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
      </summary>
      <pre>{JSON.stringify(event.payload, null, 2)}</pre>
    </details>)}
  </section>;
}

export function AdminConsole() {
  const [tab, setTab] = useState<AdminTab>("overview");
  const [forbidden, setForbidden] = useState(false);
  const [checked, setChecked] = useState(false);
  const [period, setPeriod] = useState<Period>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [barError, setBarError] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const onBarError = useCallback((message: string) => setBarError(message), []);
  const onMeta = useCallback((value: string | null) => setRefreshedAt(value), []);
  const sync = async () => {
    setSyncing(true);
    setBarError("");
    try {
      await api("/api/admin/spend-sync", { method: "POST", body: JSON.stringify({ days: 90 }) });
      setRefreshKey((value) => value + 1);
    } catch (caught) { setBarError(caught instanceof Error ? caught.message : "The spend sync failed."); }
    finally { setSyncing(false); }
  };

  useEffect(() => {
    let cancelled = false;
    api("/api/admin/workspaces")
      .then(() => { if (!cancelled) { setForbidden(false); setChecked(true); } })
      .catch((caught) => {
        if (cancelled) return;
        const message = caught instanceof Error ? caught.message : "";
        setForbidden(message.includes("platform administrators"));
        setChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (checked && forbidden) {
    return <div className="admin-gate admin-gate--inline">
      <ShieldAlert />
      <h1>Administrators only</h1>
      <p>This account does not have platform admin access.</p>
    </div>;
  }

  return <div className="admin-console">
    <nav className="admin-tabs" role="tablist" aria-label="Admin views">
      <button role="tab" type="button" aria-selected={tab === "overview"} className={tab === "overview" ? "is-active" : ""} onClick={() => setTab("overview")}><BarChart3 />Overview</button>
      <button role="tab" type="button" aria-selected={tab === "spenders"} className={tab === "spenders" ? "is-active" : ""} onClick={() => setTab("spenders")}><CreditCard />Top spenders</button>
      <button role="tab" type="button" aria-selected={tab === "change"} className={tab === "change" ? "is-active" : ""} onClick={() => setTab("change")}><TrendingDown />Spend change</button>
      <button role="tab" type="button" aria-selected={tab === "failures"} className={tab === "failures" ? "is-active" : ""} onClick={() => setTab("failures")}><AlertTriangle />Payment failures</button>
      <button role="tab" type="button" aria-selected={tab === "users"} className={tab === "users" ? "is-active" : ""} onClick={() => setTab("users")}><Users />Users</button>
      <button role="tab" type="button" aria-selected={tab === "workspaces"} className={tab === "workspaces" ? "is-active" : ""} onClick={() => setTab("workspaces")}><Building2 />Ad accounts</button>
      <button role="tab" type="button" aria-selected={tab === "events"} className={tab === "events" ? "is-active" : ""} onClick={() => setTab("events")}><Webhook />Events</button>
      <button role="tab" type="button" aria-selected={tab === "admins"} className={tab === "admins" ? "is-active" : ""} onClick={() => setTab("admins")}><ShieldCheck />Administrators</button>
    </nav>
    {tab !== "admins" && tab !== "workspaces" && tab !== "users" && <div className="admin-controls">
      <PeriodBar period={period} onChange={setPeriod} range={range} />
      <div className="admin-controls__actions">
        {refreshedAt && <span className="admin-muted" title="Spend re-syncs from the ad network every 5 minutes; this view refreshes every 2.">Updated {new Date(refreshedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>}
        {(tab === "overview" || tab === "spenders" || tab === "failures") && <button className="button button--secondary button--small" type="button" onClick={() => setReportOpen(true)}><Download /> Export report</button>}
        <button className="button button--secondary button--small" type="button" disabled={syncing} title="Pull the last 90 days of spend from the ad network now" onClick={() => void sync()}>{syncing ? <><LoaderCircle className="spin" /> Syncing…</> : <><RefreshCw /> Sync</>}</button>
      </div>
    </div>}
    {barError && <div className="admin-alert" role="alert">{barError}</div>}
    {reportOpen && <ReportBuilder period={period} onClose={() => setReportOpen(false)} onError={onBarError} />}
    {tab === "overview" && <OverviewView period={period} refreshKey={refreshKey} onRange={setRange} onMeta={onMeta} />}
    {tab === "spenders" && <SpendersView period={period} refreshKey={refreshKey} />}
    {tab === "change" && <SpendChangeView refreshKey={refreshKey} />}
    {tab === "failures" && <FailuresView period={period} />}
    {tab === "users" && <UsersView />}
    {tab === "workspaces" && <WorkspacesView />}
    {tab === "events" && <EventsView period={period} />}
    {tab === "admins" && <AdminsView />}
  </div>;
}
